import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { cancelamentosAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  FileX, 
  Loader2, 
  Filter, 
  RefreshCw, 
  Pencil,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle
} from 'lucide-react';

const Cancelamentos = () => {
  const { user } = useAuth();
  const [cancelamentos, setCancelamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estatisticas, setEstatisticas] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [editando, setEditando] = useState(false);
  const [selecionado, setSelecionado] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    carregarDados();
  }, [filtroStatus]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus && filtroStatus !== 'todos') params.status_bko = filtroStatus;

      const [listRes, statsRes] = await Promise.all([
        cancelamentosAPI.listar(params),
        cancelamentosAPI.estatisticas()
      ]);
      setCancelamentos(listRes.data);
      setEstatisticas(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar cancelamentos:', error);
      toast.error('Erro ao carregar cancelamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async () => {
    if (!selecionado) return;
    setSalvando(true);
    try {
      await cancelamentosAPI.atualizar(selecionado.id, {
        status_bko: selecionado.status_bko,
        data_desconexao: selecionado.data_desconexao || '',
        protocolo_cancelamento: selecionado.protocolo_cancelamento || '',
        observacao: selecionado.observacao || ''
      });
      toast.success('Cancelamento atualizado!');
      setEditando(false);
      setSelecionado(null);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Pendente':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pendente</Badge>;
      case 'Em Andamento':
        return <Badge className="bg-blue-100 text-blue-800"><AlertTriangle className="h-3 w-3 mr-1" />Em Andamento</Badge>;
      case 'Concluido':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Concluido</Badge>;
      case 'Cancelado':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Cancelado</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="cancelamentos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileX className="h-7 w-7 text-red-600" />
            Cancelamentos BKO
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Cancelamentos gerados automaticamente por vendas TT
          </p>
        </div>
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-5 gap-4" data-testid="cancelamentos-stats">
          <Card className="bg-gray-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-900">{estatisticas.total}</div>
              <div className="text-sm text-gray-500">Total</div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{estatisticas.pendente}</div>
              <div className="text-sm text-yellow-600">Pendentes</div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{estatisticas.em_andamento}</div>
              <div className="text-sm text-blue-600">Em Andamento</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{estatisticas.concluido}</div>
              <div className="text-sm text-green-600">Concluidos</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{estatisticas.cancelado}</div>
              <div className="text-sm text-red-600">Cancelados</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <Filter className="h-5 w-5 text-gray-400" />
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-64" data-testid="filtro-status-cancelamento">
                <SelectValue placeholder="Filtrar por Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="Pendente">Pendente</SelectItem>
                <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                <SelectItem value="Concluido">Concluido</SelectItem>
                <SelectItem value="Cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={carregarDados} data-testid="btn-atualizar-cancelamentos">
              <RefreshCw className="mr-2 h-4 w-4" />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <CardTitle>Cancelamentos ({cancelamentos.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" />
              <p className="text-gray-500 mt-2">Carregando...</p>
            </div>
          ) : cancelamentos.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <FileX className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Nenhum cancelamento encontrado</p>
              <p className="text-xs text-gray-400 mt-1">Cancelamentos sao gerados automaticamente quando uma venda TT e registrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="cancelamentos-table">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">Protocolo Venda</th>
                    <th className="text-left p-3 font-medium text-gray-600">Contrato Antigo</th>
                    <th className="text-left p-3 font-medium text-gray-600">Titular Antigo</th>
                    <th className="text-left p-3 font-medium text-gray-600">Telefone</th>
                    <th className="text-left p-3 font-medium text-gray-600">Vendedor</th>
                    <th className="text-left p-3 font-medium text-gray-600">Status</th>
                    <th className="text-left p-3 font-medium text-gray-600">Protocolo Cancel.</th>
                    <th className="text-left p-3 font-medium text-gray-600">Tratado por</th>
                    <th className="text-right p-3 font-medium text-gray-600">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {cancelamentos.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm font-semibold text-gray-900">{item.venda_protocolo}</td>
                      <td className="p-3 text-gray-700">{item.contrato_antigo || '-'}</td>
                      <td className="p-3 text-gray-700 font-medium">{item.nome_titular_antigo || '-'}</td>
                      <td className="p-3 text-gray-700 text-sm">{item.telefone_titular_antigo || '-'}</td>
                      <td className="p-3 text-gray-700 text-sm">{item.vendedor_nome}</td>
                      <td className="p-3">{getStatusBadge(item.status_bko)}</td>
                      <td className="p-3 text-gray-700 text-sm">{item.protocolo_cancelamento || '-'}</td>
                      <td className="p-3 text-gray-700 text-sm">{item.tratado_por || '-'}</td>
                      <td className="p-3 text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelecionado({ ...item });
                            setEditando(true);
                          }}
                          data-testid={`editar-cancelamento-${item.id}`}
                        >
                          <Pencil className="h-4 w-4 text-blue-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Editar */}
      <Dialog open={editando} onOpenChange={setEditando}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Tratar Cancelamento
            </DialogTitle>
          </DialogHeader>
          {selecionado && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">Venda: {selecionado.venda_protocolo}</p>
                <p className="text-sm text-gray-500">
                  Titular: {selecionado.nome_titular_antigo || '-'} | Contrato: {selecionado.contrato_antigo || '-'}
                </p>
                <p className="text-sm text-gray-500">Vendedor: {selecionado.vendedor_nome}</p>
              </div>

              <div>
                <Label>Status BKO</Label>
                <Select value={selecionado.status_bko} onValueChange={(v) => setSelecionado({...selecionado, status_bko: v})}>
                  <SelectTrigger className="mt-1" data-testid="select-status-bko">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pendente">Pendente</SelectItem>
                    <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                    <SelectItem value="Concluido">Concluido</SelectItem>
                    <SelectItem value="Cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data Desconexao</Label>
                  <Input
                    type="date"
                    value={selecionado.data_desconexao || ''}
                    onChange={(e) => setSelecionado({...selecionado, data_desconexao: e.target.value})}
                    className="mt-1"
                    data-testid="input-data-desconexao"
                  />
                </div>
                <div>
                  <Label>Protocolo Cancelamento</Label>
                  <Input
                    value={selecionado.protocolo_cancelamento || ''}
                    onChange={(e) => setSelecionado({...selecionado, protocolo_cancelamento: e.target.value})}
                    placeholder="Ex: PROT-12345"
                    className="mt-1"
                    data-testid="input-protocolo-cancelamento"
                  />
                </div>
              </div>

              <div>
                <Label>Observacao</Label>
                <Textarea
                  value={selecionado.observacao || ''}
                  onChange={(e) => setSelecionado({...selecionado, observacao: e.target.value})}
                  placeholder="Observacoes sobre o cancelamento..."
                  rows={3}
                  className="mt-1"
                  data-testid="textarea-obs-cancelamento"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditando(false); setSelecionado(null); }}>
              Cancelar
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSalvar}
              disabled={salvando}
              data-testid="btn-salvar-cancelamento"
            >
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cancelamentos;
